import type {
  MilestoneDay,
  OnboardingResource,
  OnboardingResourceBehavior,
  OnboardingResourceCategory,
  OnboardingResourceRenderType,
} from "@shared/schema";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Check, ChevronDown, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { CATEGORY_OPTIONS } from "@/lib/category-labels";
import { orpc } from "@/lib/rpc";
import type { AssignmentWithResource } from "./data-source";
import { labelForRenderType } from "./render-type-labels";

type CreateKind = "todo" | "content" | "slack" | "tool";
type ContentType = "link" | "notion";
type ModalView = "list" | CreateKind;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  milestoneDay: MilestoneDay;
  resources: OnboardingResource[];
  assignments: AssignmentWithResource[];
  onPick: (resource: OnboardingResource) => Promise<void> | void;
  onResourceCreated: () => void;
};

const KIND_HEADER: Record<CreateKind, string> = {
  todo: "Novo to-do",
  content: "Novo conteúdo",
  slack: "Novos canais do Slack",
  tool: "Novas ferramentas",
};

const CONTENT_TYPE_CONFIG: Record<
  ContentType,
  {
    label: string;
    behavior: OnboardingResourceBehavior;
    renderType: OnboardingResourceRenderType;
  }
> = {
  link: { label: "Link externo", behavior: "redirect", renderType: "rich_doc" },
  notion: { label: "Página do Notion", behavior: "composed", renderType: "notion_mirror" },
};

function isValidHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function AddResourceModal({
  open,
  onOpenChange,
  milestoneDay,
  resources,
  assignments,
  onPick,
  onResourceCreated,
}: Props) {
  const { toast } = useToast();
  const [view, setView] = useState<ModalView>("list");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<Set<OnboardingResourceCategory>>(
    () => new Set(),
  );

  function reset() {
    setView("list");
    setSearch("");
    setCategoryFilter(new Set());
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  const assignedByResourceId = useMemo(() => {
    const map = new Map<string, AssignmentWithResource>();
    for (const a of assignments) map.set(a.resourceId, a);
    return map;
  }, [assignments]);

  const { available, used } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const av: OnboardingResource[] = [];
    const ud: { resource: OnboardingResource; day: number }[] = [];
    for (const r of resources) {
      if (q.length > 0 && !r.title.toLowerCase().includes(q)) continue;
      if (categoryFilter.size > 0) {
        if (!r.category || !categoryFilter.has(r.category as OnboardingResourceCategory)) continue;
      }
      const assignment = assignedByResourceId.get(r.id);
      if (assignment) {
        ud.push({ resource: r, day: assignment.milestoneDay });
      } else {
        av.push(r);
      }
    }
    return { available: av, used: ud };
  }, [resources, search, categoryFilter, assignedByResourceId]);

  async function handlePick(resource: OnboardingResource) {
    try {
      await onPick(resource);
      toast({ title: `"${resource.title}" adicionado ao Dia ${milestoneDay}` });
    } catch {
      // parent shows error toast
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[760px] w-[92vw] p-0 gap-0 overflow-hidden"
        data-testid="add-resource-modal"
      >
        <div className="px-6 pt-5 pb-3 border-b border-border">
          <DialogTitle className="text-base font-semibold">
            {view === "list" ? `Adicionar ao Dia ${milestoneDay}` : KIND_HEADER[view]}
          </DialogTitle>
        </div>

        {view === "list" ? (
          <ListView
            search={search}
            setSearch={setSearch}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            available={available}
            used={used}
            onStartCreate={(kind) => setView(kind)}
            onPick={handlePick}
            onClose={() => handleOpenChange(false)}
          />
        ) : view === "slack" ? (
          <CreateSlackView
            onBack={() => setView("list")}
            onCreated={async (resource) => {
              onResourceCreated();
              await handlePick(resource);
              setView("list");
            }}
          />
        ) : view === "tool" ? (
          <CreateToolView
            onBack={() => setView("list")}
            onCreated={async (resource) => {
              onResourceCreated();
              await handlePick(resource);
              setView("list");
            }}
          />
        ) : (
          <CreateView
            kind={view as Exclude<CreateKind, "slack" | "tool">}
            milestoneDay={milestoneDay}
            onBack={() => setView("list")}
            onCreated={async (resource) => {
              onResourceCreated();
              await handlePick(resource);
              setView("list");
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ListView({
  search,
  setSearch,
  categoryFilter,
  setCategoryFilter,
  available,
  used,
  onStartCreate,
  onPick,
  onClose,
}: {
  search: string;
  setSearch: (v: string) => void;
  categoryFilter: Set<OnboardingResourceCategory>;
  setCategoryFilter: (v: Set<OnboardingResourceCategory>) => void;
  available: OnboardingResource[];
  used: { resource: OnboardingResource; day: number }[];
  onStartCreate: (kind: CreateKind) => void;
  onPick: (resource: OnboardingResource) => void;
  onClose: () => void;
}) {
  const filterLabel =
    categoryFilter.size === 0
      ? "Categoria"
      : categoryFilter.size === 1
        ? (CATEGORY_OPTIONS.find((o) => categoryFilter.has(o.value))?.label ?? "Categoria")
        : `${categoryFilter.size} categorias`;

  function toggleCategory(value: OnboardingResourceCategory) {
    const next = new Set(categoryFilter);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setCategoryFilter(next);
  }

  return (
    <>
      <div className="px-6 py-3 flex gap-2">
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar conteúdo existente..."
          data-testid="add-resource-search"
          className="flex-1"
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-[180px] shrink-0 justify-between font-normal"
              data-testid="add-resource-category-filter"
            >
              <span className={categoryFilter.size === 0 ? "text-muted-foreground" : undefined}>
                {filterLabel}
              </span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[220px] p-1" align="end">
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="text-xs font-medium text-muted-foreground">Categorias</span>
              {categoryFilter.size > 0 && (
                <button
                  type="button"
                  onClick={() => setCategoryFilter(new Set())}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="add-resource-category-clear"
                >
                  Limpar
                </button>
              )}
            </div>
            <ul className="max-h-[240px] overflow-y-auto">
              {CATEGORY_OPTIONS.map((opt) => {
                const checked = categoryFilter.has(opt.value);
                return (
                  <li key={opt.value}>
                    <label
                      className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                      data-testid={`add-resource-category-option-${opt.value}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleCategory(opt.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </PopoverContent>
        </Popover>
      </div>

      <div className="px-6 pb-3 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onStartCreate("todo")}
          data-testid="add-resource-create-todo"
        >
          + To-do
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onStartCreate("content")}
          data-testid="add-resource-create-content"
        >
          + Conteúdo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onStartCreate("slack")}
          data-testid="add-resource-create-slack"
        >
          + Canal do Slack
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onStartCreate("tool")}
          data-testid="add-resource-create-tool"
        >
          + Ferramenta
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto max-h-[50vh] min-h-0">
        {available.length === 0 && used.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">
            Nenhum recurso encontrado.
          </div>
        ) : null}

        {available.length > 0 && (
          <>
            <div className="px-6 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Existentes
            </div>
            <ul>
              {available.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => onPick(r)}
                    className="w-full text-left px-6 py-2.5 hover:bg-secondary transition-colors flex items-center gap-3"
                    data-testid={`add-resource-pick-${r.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {labelForRenderType(r.renderType)}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">+</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {used.length > 0 && (
          <>
            <div className="px-6 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-t border-border mt-2">
              Já na jornada
            </div>
            <ul>
              {used.map(({ resource: r, day }) => (
                <li
                  key={r.id}
                  className="px-6 py-2.5 flex items-center gap-3 opacity-50"
                  data-testid={`add-resource-used-${r.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {labelForRenderType(r.renderType)} · Dia {day}
                    </div>
                  </div>
                  <Check className="h-4 w-4 text-[hsl(var(--forest-pulse))]" />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="px-6 py-3 border-t border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {available.length === 1 ? "1 disponível" : `${available.length} disponíveis`}
        </span>
        <Button variant="outline" size="sm" onClick={onClose} data-testid="add-resource-close">
          Fechar
        </Button>
      </div>
    </>
  );
}

function CreateView({
  kind,
  milestoneDay: _milestoneDay,
  onBack,
  onCreated,
}: {
  kind: Exclude<CreateKind, "slack" | "tool">;
  milestoneDay: MilestoneDay;
  onBack: () => void;
  onCreated: (resource: OnboardingResource) => Promise<void>;
}) {
  const { toast } = useToast();
  const [contentType, setContentType] = useState<ContentType>("link");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [todoDescription, setTodoDescription] = useState("");
  const [category, setCategory] = useState<OnboardingResourceCategory | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const needsUrl = kind === "content";
  const isNotion = kind === "content" && contentType === "notion";

  const createMutation = useMutation(orpc.onboardingResources.create.mutationOptions());

  const trimmedTitle = title.trim();
  const trimmedUrl = url.trim();
  const titleError = submitted && trimmedTitle.length === 0;
  const urlError = submitted && needsUrl && !isValidHttpUrl(trimmedUrl);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (trimmedTitle.length === 0) return;
    if (needsUrl && !isValidHttpUrl(trimmedUrl)) return;

    const disciplines = ["company"];
    const contentBlocks =
      kind === "todo"
        ? [
            {
              type: "todo" as const,
              description: todoDescription.trim() || null,
              link: null,
              dueRule: null,
              dueDate: null,
            },
          ]
        : undefined;
    const behavior = kind === "todo" ? "composed" : CONTENT_TYPE_CONFIG[contentType].behavior;
    const renderType = kind === "todo" ? "todo" : CONTENT_TYPE_CONFIG[contentType].renderType;

    try {
      const result = await createMutation.mutateAsync({
        title: trimmedTitle,
        behavior,
        renderType,
        sourceUrl: needsUrl ? trimmedUrl : null,
        sourceRefs: isNotion ? [{ url: trimmedUrl, type: "notion" as const }] : undefined,
        contentBlocks,
        scope: "company",
        templateId: null,
        companySectionKey: null,
        disciplines,
        category,
      });
      await onCreated(result.resource);
    } catch (err) {
      toast({
        title: "Não foi possível criar o recurso",
        description: (err as { message?: string } | null)?.message,
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col max-h-[75vh]">
      <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="add-resource-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar
        </button>

        <div className="space-y-2">
          <Label htmlFor="add-create-title">Título</Label>
          <Input
            id="add-create-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={
              kind === "todo" ? "O que o colaborador precisa fazer?" : "Ex: Handbook do time"
            }
            autoFocus
            data-testid="add-create-title"
          />
          {titleError && <p className="text-xs text-destructive">Título é obrigatório.</p>}
        </div>

        {kind === "content" && (
          <div className="space-y-2">
            <Label htmlFor="add-create-type">Tipo</Label>
            <Select value={contentType} onValueChange={(v) => setContentType(v as ContentType)}>
              <SelectTrigger id="add-create-type" data-testid="add-create-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="link">{CONTENT_TYPE_CONFIG.link.label}</SelectItem>
                <SelectItem value="notion">{CONTENT_TYPE_CONFIG.notion.label}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {needsUrl && (
          <div className="space-y-2">
            <Label htmlFor="add-create-url">{isNotion ? "URL da página Notion" : "URL"}</Label>
            <Input
              id="add-create-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              data-testid="add-create-url"
            />
            {urlError && (
              <p className="text-xs text-destructive">Informe uma URL http(s) válida.</p>
            )}
          </div>
        )}

        {kind === "todo" && (
          <div className="space-y-2">
            <Label htmlFor="add-create-desc">Descrição (opcional)</Label>
            <Textarea
              id="add-create-desc"
              value={todoDescription}
              onChange={(e) => setTodoDescription(e.target.value)}
              placeholder="Detalhes adicionais..."
              rows={3}
              data-testid="add-create-desc"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label>Categoria</Label>
          <CategoryPicker value={category} onChange={setCategory} />
          <p className="text-xs text-muted-foreground">
            Categorias são labels de orientação. Todo conteúdo fica visível para todos.
          </p>
        </div>
      </div>

      <div className="px-6 py-3 border-t border-border flex justify-end gap-2 shrink-0">
        <Button type="button" variant="ghost" onClick={onBack} disabled={createMutation.isPending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={createMutation.isPending} data-testid="add-create-submit">
          {createMutation.isPending ? "Adicionando..." : "Adicionar"}
        </Button>
      </div>
    </form>
  );
}

type ChannelDraft = { name: string; description: string; nameTouched: boolean };

function CreateSlackView({
  onBack,
  onCreated,
}: {
  onBack: () => void;
  onCreated: (resource: OnboardingResource) => Promise<void>;
}) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [channels, setChannels] = useState<ChannelDraft[]>([
    { name: "", description: "", nameTouched: false },
  ]);
  const [submitted, setSubmitted] = useState(false);

  const createMutation = useMutation(orpc.onboardingResources.create.mutationOptions());

  const trimmedTitle = title.trim();
  const titleError = (titleTouched || submitted) && trimmedTitle.length === 0;
  const validChannels = channels
    .map((c) => ({ name: c.name.trim(), description: c.description.trim() }))
    .filter((c) => c.name.length > 0);
  const channelsError = submitted && validChannels.length === 0;

  function updateChannel(index: number, patch: Partial<ChannelDraft>) {
    setChannels((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  }

  function addChannel() {
    setChannels((prev) => [...prev, { name: "", description: "", nameTouched: false }]);
  }

  function removeChannel(index: number) {
    setChannels((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (trimmedTitle.length === 0 || validChannels.length === 0) return;

    try {
      const result = await createMutation.mutateAsync({
        title: trimmedTitle,
        behavior: "composed",
        renderType: "slack_channel_info",
        sourceUrl: null,
        slackChannels: validChannels.map((c) => ({
          name: c.name,
          description: c.description.length > 0 ? c.description : null,
        })),
        scope: "company",
        templateId: null,
        companySectionKey: null,
        disciplines: ["company"],
        category: null,
      });
      await onCreated(result.resource);
    } catch (err) {
      toast({
        title: "Não foi possível criar o recurso",
        description: (err as { message?: string } | null)?.message,
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col max-h-[75vh]">
      <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="add-resource-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar
        </button>

        <div className="space-y-2">
          <Label htmlFor="add-slack-title">Título do recurso</Label>
          <Input
            id="add-slack-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => setTitleTouched(true)}
            placeholder="Ex: Canais essenciais do time"
            autoFocus
            data-testid="add-slack-title"
          />
          {titleError && <p className="text-xs text-destructive">Título é obrigatório.</p>}
        </div>

        <div className="space-y-2">
          <Label>Canais</Label>
          <div className="space-y-3">
            {channels.map((channel, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: drafts only exist in this form
                key={index}
                className="rounded-md border border-border p-3 space-y-2"
                data-testid={`add-slack-channel-${index}`}
              >
                <div className="flex items-center gap-2">
                  <Input
                    value={channel.name}
                    onChange={(e) => updateChannel(index, { name: e.target.value })}
                    onBlur={() => updateChannel(index, { nameTouched: true })}
                    placeholder="#nome-do-canal"
                    data-testid={`add-slack-channel-name-${index}`}
                  />
                  {channels.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeChannel(index)}
                      aria-label="Remover canal"
                      data-testid={`add-slack-channel-remove-${index}`}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
                {(channel.nameTouched || submitted) && channel.name.trim().length === 0 && (
                  <p className="text-xs text-destructive">Nome do canal é obrigatório.</p>
                )}
                <Textarea
                  value={channel.description}
                  onChange={(e) => updateChannel(index, { description: e.target.value })}
                  placeholder="Descrição (opcional)"
                  rows={2}
                  data-testid={`add-slack-channel-desc-${index}`}
                />
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addChannel}
            data-testid="add-slack-channel-add"
          >
            + Adicionar canal
          </Button>
          {channelsError && (
            <p className="text-xs text-destructive">Informe pelo menos um canal.</p>
          )}
        </div>
      </div>

      <div className="px-6 py-3 border-t border-border flex justify-end gap-2 shrink-0">
        <Button type="button" variant="ghost" onClick={onBack} disabled={createMutation.isPending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={createMutation.isPending} data-testid="add-slack-submit">
          {createMutation.isPending ? "Adicionando..." : "Adicionar"}
        </Button>
      </div>
    </form>
  );
}

type ToolDraft = {
  name: string;
  helpLink: string;
  description: string;
  required: boolean;
  nameTouched: boolean;
  linkTouched: boolean;
};

function CreateToolView({
  onBack,
  onCreated,
}: {
  onBack: () => void;
  onCreated: (resource: OnboardingResource) => Promise<void>;
}) {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [tools, setTools] = useState<ToolDraft[]>([
    {
      name: "",
      helpLink: "",
      description: "",
      required: true,
      nameTouched: false,
      linkTouched: false,
    },
  ]);
  const [submitted, setSubmitted] = useState(false);

  const createMutation = useMutation(orpc.onboardingResources.create.mutationOptions());

  const trimmedTitle = title.trim();
  const titleError = (titleTouched || submitted) && trimmedTitle.length === 0;
  const normalized = tools.map((t) => ({
    name: t.name.trim(),
    helpLink: t.helpLink.trim(),
    description: t.description.trim(),
    required: t.required,
  }));
  const validTools = normalized.filter((t) => t.name.length > 0);
  const toolsError = submitted && validTools.length === 0;
  const linkErrors = tools.map((t, i) => {
    const link = normalized[i].helpLink;
    const show = t.linkTouched || submitted;
    return show && link.length > 0 && !isValidHttpUrl(link);
  });
  const hasLinkError = linkErrors.some(Boolean);

  function updateTool(index: number, patch: Partial<ToolDraft>) {
    setTools((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  }

  function addTool() {
    setTools((prev) => [
      ...prev,
      {
        name: "",
        helpLink: "",
        description: "",
        required: true,
        nameTouched: false,
        linkTouched: false,
      },
    ]);
  }

  function removeTool(index: number) {
    setTools((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (trimmedTitle.length === 0 || validTools.length === 0) return;
    if (normalized.some((t) => t.helpLink.length > 0 && !isValidHttpUrl(t.helpLink))) return;

    try {
      const result = await createMutation.mutateAsync({
        title: trimmedTitle,
        behavior: "composed",
        renderType: "tool_setup",
        sourceUrl: null,
        tools: validTools.map((t) => ({
          name: t.name,
          helpLink: t.helpLink.length > 0 ? t.helpLink : null,
          description: t.description.length > 0 ? t.description : null,
          required: t.required,
        })),
        scope: "company",
        templateId: null,
        companySectionKey: null,
        disciplines: ["company"],
        category: null,
      });
      await onCreated(result.resource);
    } catch (err) {
      toast({
        title: "Não foi possível criar o recurso",
        description: (err as { message?: string } | null)?.message,
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col max-h-[75vh]">
      <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="add-resource-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Voltar
        </button>

        <div className="space-y-2">
          <Label htmlFor="add-tool-title">Título do recurso</Label>
          <Input
            id="add-tool-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => setTitleTouched(true)}
            placeholder="Ex: Ferramentas do dia 1"
            autoFocus
            data-testid="add-tool-title"
          />
          {titleError && <p className="text-xs text-destructive">Título é obrigatório.</p>}
        </div>

        <div className="space-y-2">
          <Label>Ferramentas</Label>
          <div className="space-y-3">
            {tools.map((tool, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: drafts only exist in this form
                key={index}
                className="rounded-md border border-border p-3 space-y-2"
                data-testid={`add-tool-row-${index}`}
              >
                <div className="flex items-center gap-2">
                  <Input
                    value={tool.name}
                    onChange={(e) => updateTool(index, { name: e.target.value })}
                    onBlur={() => updateTool(index, { nameTouched: true })}
                    placeholder="Nome da ferramenta"
                    data-testid={`add-tool-name-${index}`}
                  />
                  {tools.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTool(index)}
                      aria-label="Remover ferramenta"
                      data-testid={`add-tool-remove-${index}`}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
                {(tool.nameTouched || submitted) && tool.name.trim().length === 0 && (
                  <p className="text-xs text-destructive">Nome da ferramenta é obrigatório.</p>
                )}
                <Input
                  type="text"
                  inputMode="url"
                  value={tool.helpLink}
                  onChange={(e) => updateTool(index, { helpLink: e.target.value })}
                  onBlur={() => updateTool(index, { linkTouched: true })}
                  placeholder="Link de ajuda (opcional)"
                  data-testid={`add-tool-link-${index}`}
                />
                {linkErrors[index] && (
                  <p className="text-xs text-destructive">Informe uma URL http(s) válida.</p>
                )}
                <Textarea
                  value={tool.description}
                  onChange={(e) => updateTool(index, { description: e.target.value })}
                  placeholder="Descrição (opcional)"
                  rows={2}
                  data-testid={`add-tool-desc-${index}`}
                />
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <Checkbox
                    checked={tool.required}
                    onCheckedChange={(checked) => updateTool(index, { required: checked === true })}
                    data-testid={`add-tool-required-${index}`}
                  />
                  <span>Obrigatória</span>
                </label>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addTool}
            data-testid="add-tool-add"
          >
            + Adicionar ferramenta
          </Button>
          {toolsError && (
            <p className="text-xs text-destructive">Informe pelo menos uma ferramenta.</p>
          )}
        </div>
      </div>

      <div className="px-6 py-3 border-t border-border flex justify-end gap-2 shrink-0">
        <Button type="button" variant="ghost" onClick={onBack} disabled={createMutation.isPending}>
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={createMutation.isPending || hasLinkError}
          data-testid="add-tool-submit"
        >
          {createMutation.isPending ? "Adicionando..." : "Adicionar"}
        </Button>
      </div>
    </form>
  );
}

function CategoryPicker({
  value,
  onChange,
}: {
  value: OnboardingResourceCategory | null;
  onChange: (next: OnboardingResourceCategory | null) => void;
}) {
  return (
    <div className="rounded-md border border-border overflow-hidden">
      <ul className="divide-y divide-border">
        {CATEGORY_OPTIONS.map((opt) => {
          const selected = opt.value === value;
          return (
            <li key={opt.value}>
              <button
                type="button"
                onClick={() => onChange(selected ? null : opt.value)}
                className={
                  "w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors " +
                  (selected
                    ? "bg-[#feecee] font-medium text-foreground"
                    : "hover:bg-secondary text-foreground")
                }
                data-testid={`add-create-category-${opt.value}`}
              >
                <span
                  className={
                    "inline-flex h-4 w-4 items-center justify-center rounded-full border " +
                    (selected ? "border-[#ef4d56]" : "border-border")
                  }
                  aria-hidden
                >
                  {selected ? <span className="h-2 w-2 rounded-full bg-[#ef4d56]" /> : null}
                </span>
                <span>{opt.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
